// Source evidence may contain many short turns; candidate/model budgets are separate.
export const MAX_CONFIRMED_LEADER_SPANS = 500;

export interface TranscriptTurn {
  speaker: string;
  utterance: string;
  timestamp: string | null;
}

export interface TranscriptDocumentMetadata {
  title: string | null;
  date: string | null;
}

const TIMESTAMP_ONLY_PATTERN = /^(?:\[\d{1,2}:\d{2}(?::\d{2})?\]|\d{1,2}:\d{2}(?::\d{2})?)$/;

const SPEAKER_LABEL_PATTERN =
  /(?:[PCL]\d+\s+)?(?:Unidentified speaker|\[PERSON_[A-Za-z0-9]+\]|(?:Speaker|Participant|Agent)\s*\d+|(?:(?:Dr|Mr|Mrs|Ms|Prof)\.?\s+)?[A-Z][A-Za-z'’-]+(?:\s+[A-Z][A-Za-z'’-]+){0,3})/;

const LOOSE_SPEAKER_LABEL_PATTERN =
  /(?:(?:Dr|Mr|Mrs|Ms|Prof|Coach|Leader|Agent|Host|Facilitator)\.?\s+)?(?:(?:[A-Z]\.)+|[A-Z0-9](?:[A-Za-z0-9'’/-]|\.(?=[A-Za-z0-9]))*)(?:\s+(?:(?:[A-Z]\.)+|[A-Z0-9](?:[A-Za-z0-9'’/-]|\.(?=[A-Za-z0-9]))*)){0,4}/;
const SUPPORTED_SPEAKER_LABEL_PATTERN = new RegExp(`(?:${SPEAKER_LABEL_PATTERN.source}|${LOOSE_SPEAKER_LABEL_PATTERN.source})`);

const SPEAKER_TURN_PATTERN = new RegExp(
  `^(?<timestamp>(?:\\[\\d{1,2}:\\d{2}(?::\\d{2})?\\]|\\d{1,2}:\\d{2}(?::\\d{2})?)\\s+)?(?<label>${SPEAKER_LABEL_PATTERN.source})\\s*[-:–—]\\s+(?<content>.+)$`
);

const LOOSE_SPEAKER_TURN_PATTERN = new RegExp(
  `^(?<timestamp>(?:\\[\\d{1,2}:\\d{2}(?::\\d{2})?\\]|\\d{1,2}:\\d{2}(?::\\d{2})?)\\s+)?(?<label>${LOOSE_SPEAKER_LABEL_PATTERN.source})\\s*[-:–—]\\s+(?<content>.+)$`
);
const EMPTY_SPEAKER_HEADER_PATTERN = new RegExp(
  `^(?<timestamp>(?:\\[\\d{1,2}:\\d{2}(?::\\d{2})?\\]|\\d{1,2}:\\d{2}(?::\\d{2})?)\\s+)?(?<label>${SUPPORTED_SPEAKER_LABEL_PATTERN.source})\\s*[-:–—]\\s*(?<content>)$`
);

const PIPE_SPEAKER_HEADER_PATTERN = new RegExp(
  `^(?<timestamp>\\[?\\d{1,2}:\\d{2}(?::\\d{2})?\\]?)\\s*\\|\\s*(?<label>${SUPPORTED_SPEAKER_LABEL_PATTERN.source}(?=\\s*[-:–—]|\\s*$)|${LOOSE_SPEAKER_LABEL_PATTERN.source})(?:\\s*[-:–—]?\\s*(?<content>.+))?$`
);

// Source-perspective annotations do not establish the leader's identity.
const SPEAKER_FIRST_PIPE_HEADER_PATTERN = new RegExp(
  `^(?<label>${SUPPORTED_SPEAKER_LABEL_PATTERN.source})(?:\\s+\\((?:You|Others)\\))?\\s*\\|\\s*(?<timestamp>\\[?\\d{1,2}:\\d{2}(?::\\d{2})?\\]?)$`
);

function matchPipeSpeakerHeader(text: string): RegExpMatchArray | null {
  return text.match(PIPE_SPEAKER_HEADER_PATTERN) ?? text.match(SPEAKER_FIRST_PIPE_HEADER_PATTERN);
}

const METADATA_LINE_PATTERN = /^(?:date|title|meeting(?:\s+id)?|session(?:\s+id)?|simulated meeting date|speaker map|attendees?)\s*:/i;
const PLAUSIBLE_SPEECH_HEADER = new RegExp(`(?<label>${SUPPORTED_SPEAKER_LABEL_PATTERN.source})\\s*(?:(?<colon>:)\\s*|[-–—]\\s+)(?=\\S)`, 'g');
const STRUCTURED_SPEAKER_LABEL_PATTERN = new RegExp(`^${SPEAKER_LABEL_PATTERN.source}$`);
const STRUCTURED_SPEECH_HEADING = /^(?:the plan|next steps?|action items?)$/i;
const BULLET_PROSE_HEADING = /^(?:risks?|decisions?|actions?|notes?|summary|agenda|priorities|commitments?|follow[- ]?ups?)$/i;
const PRONOUN_LED_SPEECH = /^(?:I|We|You|It|This|That)(?:['’](?:m|re|ve|ll|d|s))?\s+[a-z][a-z'’]*(?:\s+[a-z][a-z'’]*)*$/;

// An unsupported physical header is a boundary, never continuation by the last
// known speaker. Missing delimiter spacing withholds identity, not the boundary.
const UNKNOWN_TURN_PATTERN = /^(?:(?<timestamp>\[\d{1,2}:\d{2}(?::\d{2})?\]|\d{1,2}:\d{2}(?::\d{2})?)\s+)?(?<heading>[^:–—]{1,80}?)\s*(?<delimiter>[-:–—])\s*(?<content>.*)$/;

/** Compact punctuation inside a sentence is not a new speaker. Keep short or
 * otherwise ambiguous compact labels fail-closed, including unsupported names. */
function matchUnknownTurnHeader(text: string): RegExpMatchArray | null {
  const match = text.match(UNKNOWN_TURN_PATTERN);
  if (!match?.groups) return match;
  const { heading, content, delimiter, timestamp } = match.groups;
  if (timestamp) return match;
  const compactWordHyphen = delimiter === '-' && /[\p{L}\p{N}]$/u.test(heading)
    && /^[\p{L}\p{N}]/u.test(content) && text.includes(`${heading}-${content}`);
  if (compactWordHyphen && heading.trim().split(/\s+/).length > 5) {
    const sentenceOpening = /^(?:(?:The|A|An)\b.*\b(?:is|was|were|are)\b|(?:Tell|Show|Explain|Help)\s+(?:me|us)\b)/.test(heading);
    const conversationalOpening = /^(?:Yeah|Yes|Okay|Well|Thanks|Thank you)\b/.test(heading)
      && /[,!?]|[\p{L}]{2,}\.(?:\s|$)/u.test(heading);
    if (sentenceOpening || conversationalOpening) return null;
  }
  return match;
}

type SessionDocumentPreamble = TranscriptDocumentMetadata & { lineIndexes: number[] };
/** A specific export's metadata is admitted only as a complete, corroborated
 * document-start tuple. Arbitrary opening prose remains unidentified speech. */
function sessionDocumentPreamble(lines: readonly string[]): SessionDocumentPreamble | null {
  const nonempty = lines.map((raw, index) => ({ text: raw.trim().replace(/\s+/g, ' '), index }))
    .filter(line => line.text);
  if (nonempty.length < 5) return null;
  const [titleLine, dateLine, legendLine, firstTurnLine] = nonempty;
  const legend = legendLine.text.match(new RegExp(
    `^Speaker labels: (?<first>${SUPPORTED_SPEAKER_LABEL_PATTERN.source}) \\(You\\) and (?<second>${SUPPORTED_SPEAKER_LABEL_PATTERN.source}) \\(Others\\)\\.$`,
  ));
  if (!legend?.groups) return null;
  const first = legend.groups.first.trim(), second = legend.groups.second.trim();
  if (first.toLowerCase() === second.toLowerCase()) return null;
  const titles = [first + ' and ' + second, second + ' and ' + first]
    .flatMap(names => [names + ' coaching session', names + ' first coaching session']);
  if (!titles.includes(titleLine.text)) return null;
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const date = dateLine.text.match(/^([A-Z][a-z]+) (\d{1,2}), ((?:19|20)\d{2})$/);
  if (!date) return null;
  const month = months.indexOf(date[1]), day = Number(date[2]), year = Number(date[3]);
  const parsed = new Date(Date.UTC(year, month, day));
  if (month < 0 || parsed.getUTCMonth() !== month || parsed.getUTCDate() !== day || parsed.getUTCFullYear() !== year) return null;
  const firstTurn = matchPipeSpeakerHeader(firstTurnLine.text);
  if (!firstTurn?.groups || ![first, second].includes(firstTurn.groups.label.trim())) return null;
  const labels = new Set(nonempty.slice(3).flatMap(line => {
    const header = matchPipeSpeakerHeader(line.text);
    return header?.groups ? [header.groups.label.trim()] : [];
  }));
  if (labels.size !== 2 || !labels.has(first) || !labels.has(second)) return null;
  return { title: titleLine.text, date: dateLine.text, lineIndexes: nonempty.slice(0, 3).map(line => line.index) };
}

/** Google Meet's complete closing export notice is document metadata, not a
 * third speaker. Require the exact terminal pair and corroborating speech;
 * never discard a named speaker quoting it or an incomplete/extended notice. */
function sessionDocumentFooterLineIndexes(lines: readonly string[]): number[] {
  const nonempty = lines.map((raw, index) => ({ text: raw.trim().replace(/\s+/g, ' '), index }))
    .filter(line => line.text);
  const footer = /^(?:Unidentified speaker: )?Transcription ended after \d{1,3}:[0-5]\d:[0-5]\d This editable transcript was computer generated and might contain errors\. People can also change the text after it was created\.$/;
  for (const count of [1, 2]) {
    const candidate = nonempty.slice(-count);
    if (!footer.test(candidate.map(line => line.text).join(' '))) continue;

    const prefix = nonempty.slice(0, -count);
    let preceding = prefix.length - 1;
    while (preceding >= 0 && TIMESTAMP_ONLY_PATTERN.test(prefix[preceding].text)) preceding -= 1;
    const precedingText = preceding >= 0 ? normalizeMarkdownLine(prefix[preceding].text) : '';
    const precedingHeader = matchPipeSpeakerHeader(precedingText)
      ?? precedingText.match(EMPTY_SPEAKER_HEADER_PATTERN)
      ?? matchUnknownTurnHeader(precedingText);
    // A header on its own makes the following text possible quoted speech,
    // including when a timestamp separates the header from its first words.
    if (precedingHeader?.groups && !precedingHeader.groups.content?.trim()) continue;

    const labels = new Set<string>();
    let pendingLabel: string | null = null;
    for (const { text } of prefix) {
      const normalized = normalizeMarkdownLine(text);
      if (isMetadataLine(text)) { pendingLabel = null; continue; }
      const header = normalized.match(SPEAKER_TURN_PATTERN)
        ?? normalized.match(LOOSE_SPEAKER_TURN_PATTERN)
        ?? matchPipeSpeakerHeader(normalized)
        ?? normalized.match(EMPTY_SPEAKER_HEADER_PATTERN);
      if (header?.groups) {
        const label = header.groups.label.trim();
        const content = header.groups.content?.trim() ?? '';
        pendingLabel = /^(?:[PCL]\d+\s+)?Unidentified speaker$/i.test(label)
          || hasPlausibleSpeechHeader(content, new Set()) ? null : label.toLowerCase();
        if (pendingLabel && content) labels.add(pendingLabel);
      } else if (matchUnknownTurnHeader(normalized) || hasPlausibleSpeechHeader(normalized, new Set())) {
        pendingLabel = null;
      } else if (pendingLabel && !TIMESTAMP_ONLY_PATTERN.test(normalized)) {
        labels.add(pendingLabel);
      }
    }
    if (labels.size >= 2) return candidate.map(line => line.index);
  }
  return [];
}

function hasPlausibleSpeechHeader(text: string, numericSpeakerLabels: ReadonlySet<string>): boolean {
  for (const match of text.matchAll(PLAUSIBLE_SPEECH_HEADER)) {
    const label = match.groups?.label ?? '';
    const following = text.slice(match.index + match[0].length);
    // A numeric colon numeric value is clock/ratio content, not another turn.
    // Keep named numbered speakers (Speaker 2, Agent 1, etc.) and numeric
    // labels followed by speech ambiguous. Physical header parsing is unchanged.
    const numericToken = label.match(/(?:^|\s)(\d+)$/)?.[1];
    const numericColonValue = match.groups?.colon && numericToken
      && /^\d+(?=$|[\s:.,;!?\])}])/.test(following)
      && !STRUCTURED_SPEAKER_LABEL_PATTERN.test(label)
      && !numericSpeakerLabels.has(numericToken);
    if (!numericColonValue) return true;
  }
  return false;
}

function isBulletedContinuation(line: string): boolean {
  if (!/^[-*+•]\s+/.test(line)) return false;
  const normalized = normalizeMarkdownLine(line);
  // Recognize structural prose, but do not absorb a supported name such as Dana.
  if (BULLET_PROSE_HEADING.test(normalized.split(':', 1)[0])) return true;
  return STRUCTURED_SPEECH_HEADING.test(normalized.split(':', 1)[0])
    || PRONOUN_LED_SPEECH.test(normalized.split(':', 1)[0])
    || METADATA_LINE_PATTERN.test(normalized) || (!matchUnknownTurnHeader(normalized) && !SPEAKER_TURN_PATTERN.test(normalized)
    && !LOOSE_SPEAKER_TURN_PATTERN.test(normalized) && !matchPipeSpeakerHeader(normalized));
}

function normalizeMarkdownLine(line: string): string {
  return mappedMarkdownLine(line,0).map(character=>character.text).join('');
}

function isSeparatorLine(line: string): boolean {
  return /^-{3,}$/.test(line.trim());
}

function isMetadataLine(line: string): boolean {
  if (isBulletedContinuation(line)) return false;
  const normalized = normalizeMarkdownLine(line);
  if (!normalized) return false;
  if (METADATA_LINE_PATTERN.test(normalized)) return true;
  return /^#/.test(line.trim()) || isSeparatorLine(line);
}

export function extractTranscriptMetadata(transcript: string): TranscriptDocumentMetadata {
  const normalized = transcript.replace(/\r\n/g, "\n");
  const preamble = sessionDocumentPreamble(normalized.split("\n"));
  let title: string | null = preamble?.title ?? null;
  let date: string | null = preamble?.date ?? null;

  for (const rawLine of normalized.split("\n")) {
    const trimmed = rawLine.trim();
    if (!trimmed) continue;
    if (/^[-*+•]\s+/.test(trimmed)) continue;

    if (!title && /^#{1,6}\s+/.test(trimmed)) {
      title = normalizeMarkdownLine(trimmed);
      continue;
    }

    const normalizedLine = normalizeMarkdownLine(trimmed);
    if (!date && /^date\s*:/i.test(normalizedLine)) {
      date = normalizedLine.replace(/^date\s*:/i, "").trim() || null;
    }
  }

  return { title, date };
}

export function hasExplicitAcronymSpeaker(transcript: string): boolean {
  return transcript.split(/\r?\n/).some((line) => {
    const normalized = normalizeMarkdownLine(line);
    const header = normalized.match(SPEAKER_TURN_PATTERN) ?? matchPipeSpeakerHeader(normalized);
    return /^(?:[PCL]\d+\s+)?(?:OK|AI)$/.test(header?.groups?.label ?? '');
  });
}

/** Physical source lines, never guessed names inside speech, establish turns. */
export function insertSpeakerLineBreaks(transcript: string, _preserveOpeningAcronymProse?: boolean): string {
  return transcript;
}

type SourceLine = { text: string; raw: string; start: number };
function normalizedTranscriptLines(text: string, canonical = false): SourceLine[] {
  let cursor=0;
  const sourceLines=text.split("\n").map(raw=>{
    const start=cursor; cursor+=Array.from(raw).length+1; return {raw,start};
  });
  const lines=sourceLines.map(line=>line.raw);
  const metadataIndexes = new Set([
    ...(sessionDocumentPreamble(lines)?.lineIndexes ?? []),
    ...sessionDocumentFooterLineIndexes(lines),
  ]);
  // Corroborated numeric source labels override the numeric-value exception.
  // Only existing physical-header grammar can establish one, never inline text.
  const numericSpeakerLabels = new Set<string>();
  for (const line of lines) {
    if (isMetadataLine(line.trim()) || isBulletedContinuation(line.trim())) continue;
    const normalized = normalizeMarkdownLine(line.trim());
    const header = normalized.match(SPEAKER_TURN_PATTERN)
      ?? normalized.match(LOOSE_SPEAKER_TURN_PATTERN)
      ?? matchPipeSpeakerHeader(normalized);
    const label = header?.groups?.label ?? '';
    if (/^\d+$/.test(label)) numericSpeakerLabels.add(label);
  }
  return sourceLines.flatMap(({raw:line,start}, index) => {
    if (metadataIndexes.has(index)) return [];
    const keep=(value:string)=>[{text:value,raw:line,start}];
    const trimmed = line.trim();
    if (!trimmed || isSeparatorLine(trimmed)) return [];
    if (isBulletedContinuation(trimmed)) return keep(trimmed);
    const normalized = normalizeMarkdownLine(trimmed);
    if (isMetadataLine(trimmed)) {
      const value = normalized.replace(METADATA_LINE_PATTERN, '');
      // Metadata is not speech. If its value contains another plausible header,
      // preserve the entire ambiguous line for general review, never recover a name.
      return hasPlausibleSpeechHeader(value, numericSpeakerLabels) ? keep(`Unidentified speaker: ${normalized}`) : [];
    }
    const header = normalized.match(SPEAKER_TURN_PATTERN)
      ?? normalized.match(LOOSE_SPEAKER_TURN_PATTERN)
      ?? matchPipeSpeakerHeader(normalized);
    if (/^(?:[PCL]\d+\s+)?Unidentified speaker$/i.test(header?.groups?.label ?? '')) return keep(normalized);
    const content = header?.groups?.content ?? '';
    if (canonical && header) return keep(normalized);
    // A second plausible header can be another speaker OR ordinary prose.
    // Keep the complete line, but do not assign it to a selected person.
    const ambiguous = hasPlausibleSpeechHeader(content, numericSpeakerLabels);
    if (ambiguous) {
      return keep(`Unidentified speaker: ${normalized}`);
    }
    return keep(normalized);
  });
}
function normalizeTranscriptSpacing(text:string,canonical=false):string {
  return normalizedTranscriptLines(text,canonical).map(line=>line.text).join("\n").trim();
}

function consumeTimestampLine(lines:readonly string[],index:number):{text:string;index:number} {
  const text=lines[index].trim();
  return TIMESTAMP_ONLY_PATTERN.test(text) && lines[index+1]
    ? {text:`${text} ${lines[index+1].trim()}`,index:index+1} : {text,index};
}

export function parseTranscriptTurns(transcript: string, options: { canonical?: boolean } = {}): TranscriptTurn[] {
  const normalizedTranscript = normalizeTranscriptSpacing(transcript, options.canonical);
  if (!normalizedTranscript) return [];

  const rawLines = normalizedTranscript.split("\n");

  const parseWithPattern = (pattern: RegExp) => {
    const turns: TranscriptTurn[] = [];

    for (let index = 0; index < rawLines.length; index += 1) {
      let trimmed = rawLines[index]?.trim() ?? "";
      if (!trimmed) continue;
      if (isBulletedContinuation(trimmed)) {
        const previousTurn = turns[turns.length - 1];
        if (previousTurn) previousTurn.utterance = `${previousTurn.utterance} ${trimmed}`.trim();
        continue;
      }

      const consumed=consumeTimestampLine(rawLines,index);
      trimmed=consumed.text;index=consumed.index;

      const pipeHeaderMatch = matchPipeSpeakerHeader(trimmed);
      if (pipeHeaderMatch?.groups) {
        turns.push({
          speaker: pipeHeaderMatch.groups.label.trim(),
          utterance: pipeHeaderMatch.groups.content?.trim() ?? "",
          timestamp: pipeHeaderMatch.groups.timestamp?.trim() ?? null,
        });
        continue;
      }

      const match = trimmed.match(pattern) ?? trimmed.match(SPEAKER_TURN_PATTERN) ?? trimmed.match(LOOSE_SPEAKER_TURN_PATTERN) ?? trimmed.match(EMPTY_SPEAKER_HEADER_PATTERN);
      if (match?.groups) {
        turns.push({
          speaker: match.groups.label.trim(),
          utterance: match.groups.content.trim(),
          timestamp: match.groups.timestamp?.trim() ?? null,
        });
        continue;
      }

      const unknown = matchUnknownTurnHeader(trimmed);
      const pendingTurn = turns[turns.length - 1];
      // Preserve familiar headings and sentence-like pronoun clauses after a pipe header.
      // Other unresolved labels must not be promoted into that speaker's words.
      if (pendingTurn && unknown
        && (STRUCTURED_SPEECH_HEADING.test(unknown.groups?.heading ?? "")
          || PRONOUN_LED_SPEECH.test(unknown.groups?.heading ?? ""))) {
        pendingTurn.utterance = `${pendingTurn.utterance} ${trimmed}`.trim();
        continue;
      }
      if (unknown?.groups) {
        turns.push({
          speaker: "Unidentified speaker",
          utterance: trimmed.slice(unknown.groups.timestamp?.length ?? 0).trim(),
          timestamp: unknown.groups.timestamp ?? null,
        });
        continue;
      }

      const previousTurn = turns[turns.length - 1];
      if (!previousTurn) {
        turns.push({ speaker: 'Unidentified speaker', utterance: trimmed, timestamp: null });
        continue;
      }
      previousTurn.utterance = `${previousTurn.utterance} ${trimmed}`.trim();
    }

    return turns;
  };

  const removeEmptyTurns = (turns: TranscriptTurn[]) => turns
    .filter((turn) => turn.utterance.trim().length > 0)
    .map((turn) => /^(?:[PCL]\d+\s+)?Unidentified speaker$/i.test(turn.speaker)
      ? { ...turn, speaker: 'Unidentified speaker' } : turn);

  const structuredTurns = removeEmptyTurns(parseWithPattern(SPEAKER_TURN_PATTERN));
  const looseTurns = removeEmptyTurns(parseWithPattern(LOOSE_SPEAKER_TURN_PATTERN));
  if (looseTurns.length > structuredTurns.length) return looseTurns;
  if (structuredTurns.length > 0) return structuredTurns;
  return looseTurns;
}


type MappedCharacter = { text:string; start:number; end:number };
/** Mirrors normalizeMarkdownLine's edits, retaining original code-point boundaries.
 * Inserted header spacing has a boundary, never invented speech ownership. */
function mappedMarkdownLine(raw:string,start:number):MappedCharacter[] {
  let chars=Array.from(raw,(text,index)=>({text,start:start+index,end:start+index+1}));
  const value=()=>chars.map(c=>c.text).join('');
  const dropPrefix=(pattern:RegExp)=>{
    const match=value().match(pattern);if(match)chars=chars.slice(Array.from(match[0]).length);
  };
  dropPrefix(/^[-*+•]\s+/);
  const bold=value().match(/^\*\*(.+?):\*\*\s*/);
  if(bold){
    const labelLength=Array.from(bold[1]).length,consumed=Array.from(bold[0]).length;
    const boundary=chars[consumed-1].end;
    chars=[...chars.slice(2,2+labelLength+1),{text:' ',start:boundary,end:boundary},...chars.slice(consumed)];
  }
  dropPrefix(/^#{1,6}\s+/);
  const unbolded:MappedCharacter[]=[];
  for(let index=0;index<chars.length;index++){
    if(chars[index].text==='*' && chars[index+1]?.text==='*')index++;
    else unbolded.push(chars[index]);
  }
  chars=unbolded;
  const collapsed:MappedCharacter[]=[];
  for(const char of chars){
    const previous=collapsed[collapsed.length-1];
    if(/\s/u.test(char.text)){
      if(previous?.text===' ')previous.end=char.end;
      else collapsed.push({...char,text:' '});
    }else collapsed.push(char);
  }
  while(collapsed[0]?.text===' ')collapsed.shift();
  while(collapsed[collapsed.length-1]?.text===' ')collapsed.pop();
  return collapsed;
}
/** Server attribution uses a stored explicit selection and the ambiguity-preserving
 * physical grammar. Returned disjoint speech spans address the ORIGINAL Unicode
 * source, including its Markdown and spacing. No model speaker claim is read. */
export function deriveConfirmedLeaderSpans(transcript:string,selectedLabel:string):{start:number;end:number}[] {
  if(typeof transcript!=='string' || transcript.length>200000 || Array.from(transcript).length>100000
    || typeof selectedLabel!=='string' || selectedLabel.length>120 || !selectedLabel.trim()
    || /^(?:[PCL]\d+\s+)?Unidentified speaker$/i.test(selectedLabel.trim()))return [];
  const selected=selectedLabel.trim(),spans:{start:number;end:number}[]=[];
  let speaker:string|null=null;
  const lines=normalizedTranscriptLines(transcript),texts=lines.map(line=>line.text),points=Array.from(transcript);
  for(let index=0;index<lines.length;index++){
    const consumed=isBulletedContinuation(texts[index])?{text:texts[index],index}:consumeTimestampLine(texts,index);
    index=consumed.index;
    const line=lines[index],text=consumed.text;
    if(TIMESTAMP_ONLY_PATTERN.test(text))continue;
    const bullet=isBulletedContinuation(text);
    const header=bullet?null:matchPipeSpeakerHeader(text)??text.match(SPEAKER_TURN_PATTERN)??text.match(LOOSE_SPEAKER_TURN_PATTERN)??text.match(EMPTY_SPEAKER_HEADER_PATTERN);
    let start:number;
    if(header?.groups){
      speaker=header.groups.label.trim();
      const content=header.groups.content??'';
      if(speaker!==selected || !content.trim())continue;
      const leading=Array.from(line.raw.slice(0,line.raw.length-line.raw.trimStart().length)).length;
      const mapped=mappedMarkdownLine(line.raw.trimStart(),line.start+leading);
      if(mapped.map(c=>c.text).join('')!==line.text)return [];
      const contentIndex=Array.from(line.text.slice(0,line.text.length-content.length)).length;
      const boundary=mapped[contentIndex-1]?.end??mapped[contentIndex]?.start;
      if(boundary===undefined)return [];start=boundary;
    }else{
      const unknown=bullet?null:matchUnknownTurnHeader(text);
      if(unknown && !(STRUCTURED_SPEECH_HEADING.test(unknown.groups!.heading)
        || PRONOUN_LED_SPEECH.test(unknown.groups!.heading))){speaker=null;continue;}
      if(speaker!==selected)continue;
      start=line.start+Array.from(line.raw.slice(0,line.raw.length-line.raw.trimStart().length)).length;
    }
    const end=line.start+Array.from(line.raw.trimEnd()).length;
    if(!Number.isSafeInteger(start) || start<line.start || end<=start)return [];
    const previous=spans[spans.length-1];
    if(previous && previous.end<=start && /^\s*$/u.test(points.slice(previous.end,start).join('')))previous.end=end;
    else spans.push({start,end});
    if(spans.length>MAX_CONFIRMED_LEADER_SPANS)return [];
  }
  return spans;
}
